import { useEffect, useRef, useState } from "react";

/**
 * ─── Wake detection ──────────────────────────────────────────────────────────
 *
 * WHY THIS EXISTS: every poller in the app (lightning, alerts, warnings, SPC,
 * tropical, and the weather keyframe refresh) is driven by setInterval. That is
 * fine while the tab is in the foreground, but it is NOT a reliable clock on a
 * phone:
 *
 *   - Backgrounded tabs are throttled to >=1 fire/minute (all browsers).
 *   - A locked phone / switched app suspends timers entirely. iOS Safari in
 *     particular freezes the whole web-content process; the interval does not
 *     "catch up" on resume, it just resumes counting from wherever it was.
 *   - A page restored from the back/forward cache (bfcache) resumes with all
 *     its JS state — and its stale data — intact, no reload.
 *
 * So a map left open on a phone all day shows whatever was on screen when the
 * screen went dark. The timer isn't broken; it just never ran.
 *
 * THE FIX: don't trust the timer, trust the device clock. We record the wall
 * clock (Date.now()) at every refresh, and on every event that signals "the
 * user is looking at this again" we compare the device clock to that stamp. If
 * more than `minHiddenMs` of real time has elapsed, we bump an epoch counter;
 * consumers put that epoch in their effect deps, which re-runs the effect and
 * triggers an immediate fetch plus a freshly-armed interval.
 *
 * This deliberately does NOT bump on a timer of its own — each consumer keeps
 * its own polling cadence (NWS alerts every 2 min, SPC every 10 min, etc.) and
 * we don't want a shared tick silently multiplying those request rates. This
 * hook only answers one question: "did we just come back from being away?"
 */
export function useWakeEpoch(minHiddenMs = 30_000): number {
  const [epoch, setEpoch] = useState(0);
  // Wall-clock stamp of the last time we considered the page "awake and
  // current". Seeded at mount so a wake event in the first few seconds after
  // load doesn't fire a redundant refresh on top of the initial fetch.
  const lastAwakeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Only meaningful when the page is actually on screen. visibilitychange
    // fires for the hide direction too; we ignore that one and just record the
    // moment so the elapsed-time math below measures time spent hidden.
    const maybeWake = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastAwakeRef.current >= minHiddenMs) {
        lastAwakeRef.current = now;
        setEpoch((e) => e + 1);
      } else {
        // Short blip (alt-tab, notification shade). Timers kept running; no
        // refresh needed, but keep the stamp moving so we measure the NEXT
        // absence from this point.
        lastAwakeRef.current = now;
      }
    };

    // pageshow catches bfcache restores, where visibilitychange may not fire.
    // online catches a device that regained connectivity while foregrounded —
    // in-flight fetches during the outage failed silently, so re-run them.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        // A bfcache restore is always a wake, regardless of elapsed time:
        // the page was frozen mid-execution.
        lastAwakeRef.current = Date.now();
        setEpoch((n) => n + 1);
      } else {
        maybeWake();
      }
    };

    document.addEventListener("visibilitychange", maybeWake);
    window.addEventListener("focus", maybeWake);
    window.addEventListener("online", maybeWake);
    window.addEventListener("pageshow", onPageShow as EventListener);

    // Resume-from-suspend also shows up as a large jump between successive
    // ticks of a slow heartbeat. This is the belt-and-braces path for Android
    // Chrome, which can keep a tab "visible" while the device is dozing and
    // therefore never fires any of the events above. 20s heartbeat is cheap
    // (a Date.now() compare) and is itself throttled while backgrounded —
    // which is exactly the signal we're looking for.
    let lastTick = Date.now();
    const heartbeat = window.setInterval(() => {
      const now = Date.now();
      const drift = now - lastTick;
      lastTick = now;
      // If the interval took much longer than scheduled, the device was
      // asleep or the tab was frozen. Treat that as a wake.
      if (drift >= minHiddenMs + 20_000) maybeWake();
    }, 20_000);

    return () => {
      document.removeEventListener("visibilitychange", maybeWake);
      window.removeEventListener("focus", maybeWake);
      window.removeEventListener("online", maybeWake);
      window.removeEventListener("pageshow", onPageShow as EventListener);
      clearInterval(heartbeat);
    };
  }, [minHiddenMs]);

  return epoch;
}
